import React from 'react'
import { intl } from 'lang/intl'
import { FormattedMessage } from 'react-intl'

import { TtAvatar, TtIcon, TtIconButton, TtBadge } from '@23/react'

import { useOverlayState, setChyron } from '../../../firebase/hooks/useOverlays'
import { useWebinarColors } from '../../hooks/useWebinarColors'
import { ChyronType } from '../../../firebase/model/chyron-model'

import GenericSpeakerList, {
  type GenericSpeakerListItemProps,
} from './GenericSpeakerList'

import './WebinarInfoSidebarSpeakerListWithChyronTrigger.scss'

const SpeakerEntry = ({ speaker }: GenericSpeakerListItemProps) => {
  const { tabs, accent } = useWebinarColors()
  const overlay = useOverlayState()
  const hasSpeakerInfoChyronShown =
    overlay &&
    overlay.type === ChyronType.SpeakerInfo &&
    overlay.liveSpeakerId === speaker.id

  const {
    id,
    title,
    company,
    name,
    avatarURL,
    link,
    linkedin,
    facebook,
    twitter,
    role,
  } = speaker

  const isHost = role === 'producer'

  const corporateIdentity = [title, company]
    .filter((p) => p !== undefined)
    .join(' • ')

  return (
    <div
      key={id}
      className="webinar-info-sidebar-speaker-list-item"
      style={{ borderBottomColor: tabs.contrast.transparent('5%') }}
    >
      <TtAvatar label={name} image={avatarURL} />
      <div className="speaker-info">
        <div className="speaker-info-name">{name}</div>
        <div className="speaker-info-subline">
          {corporateIdentity.length > 0 && (
            <div
              className="speaker-info-identity"
              style={{ color: tabs.highContrast.transparent('60%') }}
            >
              {corporateIdentity}
            </div>
          )}
          <div className="speaker-info-social">
            {link && (
              <a
                href={link}
                target="_new"
                aria-label={intl.formatMessage({
                  defaultMessage: 'website link',
                })}
              >
                <TtIcon name="globe" library="one" />
              </a>
            )}
            {linkedin && (
              <a
                href={linkedin}
                target="_new"
                aria-label={intl.formatMessage({
                  defaultMessage: 'linkedin link',
                })}
              >
                <TtIcon name="linkedin" library="one/logos" />
              </a>
            )}
            {facebook && (
              <a
                href={facebook}
                target="_new"
                aria-label={intl.formatMessage({
                  defaultMessage: 'facebook link',
                })}
              >
                <TtIcon name="facebook" library="one/logos" />
              </a>
            )}
            {twitter && (
              <a
                href={twitter}
                target="_new"
                aria-label={intl.formatMessage({
                  defaultMessage: 'twitter link',
                })}
              >
                <TtIcon name="twitter" library="one/logos" />
              </a>
            )}
          </div>
        </div>
      </div>
      {/* isProducer is not the correct property */}
      {isHost && (
        <TtBadge color="subtle">
          <FormattedMessage defaultMessage="Host" />
        </TtBadge>
      )}
      <TtIconButton
        className="speaker-chyron-button"
        name="eye"
        library="23"
        label={intl.formatMessage(
          { defaultMessage: 'Show speaker Chyron for {name}' },
          { name }
        )}
        style={hasSpeakerInfoChyronShown ? { color: accent } : undefined}
        onClick={() => {
          if (hasSpeakerInfoChyronShown) {
            setChyron(null)
          } else {
            setChyron({
              type: ChyronType.SpeakerInfo,
              name: speaker.name,
              title: speaker.title,
              avatarUrl: speaker.avatarURL,
              bio: speaker.bio,
              company: speaker.company,
              liveSpeakerId: speaker.id,
            })
          }
        }}
      />
    </div>
  )
}

const WebinarInfoSidebarSpeakerListWithChyronTrigger = () => {
  return (
    <div className="webinar-info-sidebar-speaker-list-with-chyron-trigger">
      <GenericSpeakerList
        filter={(speaker) =>
          !speaker.isHidden && speaker.creationSource !== 'guest'
        }
        renderItem={({ speaker }) => (
          <SpeakerEntry key={speaker.id} speaker={speaker} />
        )}
        placeholder={intl.formatMessage({
          defaultMessage: 'No speakers have been added to this event yet.',
        })}
      />
    </div>
  )
}

export default WebinarInfoSidebarSpeakerListWithChyronTrigger
